import { useNode } from "@craftjs/core";
import React, { ReactNode, useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Box, IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { SelectField, SliderField, CheckboxField, ColorField, SettingsSection, SettingsRow } from "../ui/form/SettingsField";

// Mobile Sidebar Component - uses portal to render in preview container
interface MobileSidebarProps {
  backgroundColor: string;
  textColor: string;
  menuChildren: React.ReactNode[];
  onClose: () => void;
}

const MobileSidebar: React.FC<MobileSidebarProps> = ({ backgroundColor, textColor, menuChildren, onClose }) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Find the preview container or fall back to body
    const previewContainer = document.querySelector(".preview-container") as HTMLElement;
    setContainer(previewContainer || document.body);
    // Trigger animation after mount
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for animation to complete
  };

  if (!container) return null;

  const sidebar = (
    <>
      {/* Backdrop */}
      <Box
        onClick={handleClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.5)",
          zIndex: 1100,
          opacity: isVisible ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      {/* Sidebar */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "80%",
          maxWidth: "300px",
          bgcolor: backgroundColor,
          boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
          zIndex: 1200,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s ease-in-out",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <IconButton onClick={handleClose} aria-label="Close menu">
              <CloseIcon sx={{ color: textColor }} />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {menuChildren.map((child, index) => (
              <Box key={index} onClick={handleClose}>
                {child}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );

  return ReactDOM.createPortal(sidebar, container);
};

export interface FlexNavbarProps {
  children?: ReactNode;
  backgroundColor?: string;
  textColor?: string;
  sticky?: boolean;
  maxWidth?: string;
  paddingX?: number;
  paddingY?: number;
  gap?: number;
  mobileBreakpoint?: number;
}

// FlexNavbar - Simple flex container for navbar components
// Children: NavLogo, NavMenu, NavCTA (each handles its own positioning)
export const FlexNavbar = ({
  children,
  backgroundColor = "#ffffff",
  textColor = "#1f2937",
  sticky = true,
  maxWidth = "1280px",
  paddingX = 24,
  paddingY = 12,
  gap = 16,
  mobileBreakpoint = 768,
}: FlexNavbarProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  // Detect mobile based on container width
  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;

    const checkWidth = () => {
      let container: HTMLElement | null = navElement;
      while (container && container.parentElement) {
        const style = window.getComputedStyle(container.parentElement);
        if (style.containerType === "inline-size" || style.containerType === "size") {
          container = container.parentElement;
          break;
        }
        container = container.parentElement;
      }
      const width = container?.clientWidth || navElement.clientWidth;
      setIsMobile(width < mobileBreakpoint);
    };

    checkWidth();
    const observer = new ResizeObserver(checkWidth);
    observer.observe(navElement);

    let parent = navElement.parentElement;
    while (parent && parent !== document.body) {
      observer.observe(parent);
      parent = parent.parentElement;
    }

    return () => observer.disconnect();
  }, [mobileBreakpoint]);

  useEffect(() => {
    if (!isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  const childArray = React.Children.toArray(children);
  const hasChildren = childArray.length > 0;

  // First child = Logo, rest = menu items
  const logoChild = childArray[0];
  const menuChildren = childArray.slice(1);

  return (
    <Box
      component="nav"
      ref={(ref: HTMLElement | null) => {
        if (ref) {
          connect(drag(ref));
          (navRef as React.MutableRefObject<HTMLElement | null>).current = ref;
        }
      }}
      sx={{
        width: "100%",
        bgcolor: backgroundColor,
        color: textColor,
        position: sticky ? "sticky" : "relative",
        top: sticky ? 0 : "auto",
        zIndex: 1000,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        ...(selected && { ...selectedOutlineSx, outlineOffset: "-2px" }),
      }}
    >
      <Box
        sx={{
          maxWidth: maxWidth,
          mx: "auto",
          px: `${paddingX}px`,
          py: `${paddingY}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: `${gap}px`,
          minHeight: "56px",
        }}
      >
        {hasChildren ? (
          <>
            {/* Logo */}
            {logoChild && <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>{logoChild}</Box>}

            {/* Desktop Menu */}
            {!isMobile && menuChildren.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: `${gap}px`, flexWrap: "nowrap", flexShrink: 0 }}>
                {menuChildren}
              </Box>
            )}

            {/* Mobile Hamburger */}
            {isMobile && menuChildren.length > 0 && (
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{ color: textColor }}
                aria-label="Open menu"
              >
                <MenuIcon />
              </IconButton>
            )}
          </>
        ) : (
          <Box
            sx={{
              width: "100%",
              py: 1,
              textAlign: "center",
              color: "#9ca3af",
              fontSize: "14px",
              border: "1px dashed #d1d5db",
              borderRadius: "4px",
            }}
          >
            ลาก NavLogo, NavMenu, NavCTA มาวางที่นี่
          </Box>
        )}
      </Box>

      {/* Mobile Sidebar - rendered via portal to preview container */}
      {isMobile && mobileMenuOpen && (
        <MobileSidebar
          backgroundColor={backgroundColor}
          textColor={textColor}
          menuChildren={menuChildren}
          onClose={() => setMobileMenuOpen(false)}
        />
      )}
    </Box>
  );
};

export const FlexNavbarSettings = () => {
  const {
    actions: { setProp },
    backgroundColor,
    textColor,
    sticky,
    maxWidth,
    paddingX,
    paddingY,
    gap,
    mobileBreakpoint,
  } = useNode((node) => ({
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    sticky: node.data.props.sticky,
    maxWidth: node.data.props.maxWidth,
    paddingX: node.data.props.paddingX,
    paddingY: node.data.props.paddingY,
    gap: node.data.props.gap,
    mobileBreakpoint: node.data.props.mobileBreakpoint,
  }));

  return (
    <SettingsSection>
      <CheckboxField
        label="Sticky Navigation"
        checked={sticky}
        onChange={(v) => setProp((props: FlexNavbarProps) => (props.sticky = v))}
      />

      <SelectField
        label="Max Width"
        value={maxWidth}
        onChange={(v) => setProp((props: FlexNavbarProps) => (props.maxWidth = v))}
        options={[
          { value: "100%", label: "Full Width" },
          { value: "1280px", label: "1280px" },
          { value: "1024px", label: "1024px" },
          { value: "768px", label: "768px" },
        ]}
      />

      <SliderField
        label="Mobile Breakpoint"
        value={mobileBreakpoint}
        onChange={(v) => setProp((props: FlexNavbarProps) => (props.mobileBreakpoint = v))}
        min={320}
        max={1024}
        step={8}
        unit="px"
      />

      <SettingsRow>
        <SliderField
          label="Padding X"
          value={paddingX}
          onChange={(v) => setProp((props: FlexNavbarProps) => (props.paddingX = v))}
          min={0}
          max={48}
          unit="px"
        />
        <SliderField
          label="Padding Y"
          value={paddingY}
          onChange={(v) => setProp((props: FlexNavbarProps) => (props.paddingY = v))}
          min={0}
          max={24}
          unit="px"
        />
      </SettingsRow>

      <SliderField
        label="Gap"
        value={gap}
        onChange={(v) => setProp((props: FlexNavbarProps) => (props.gap = v))}
        min={0}
        max={48}
        unit="px"
      />

      <SettingsRow>
        <ColorField
          label="Background"
          value={backgroundColor}
          onChange={(v) => setProp((props: FlexNavbarProps) => (props.backgroundColor = v))}
        />
        <ColorField
          label="Text Color"
          value={textColor}
          onChange={(v) => setProp((props: FlexNavbarProps) => (props.textColor = v))}
        />
      </SettingsRow>
    </SettingsSection>
  );
};

FlexNavbar.craft = {
  displayName: "Flex Navbar",
  isCanvas: true,
  props: {
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    sticky: true,
    maxWidth: "1280px",
    paddingX: 24,
    paddingY: 12,
    gap: 16,
    mobileBreakpoint: 768,
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
  },
  related: {
    settings: FlexNavbarSettings,
  },
};
